import { z } from 'zod';
import { preprocessEmptyStringTo, validators } from '@/utils/form-utils';

export const ROLE_IN_COMPANY = [
  'AI/ML Engineer',
  'Search Engineer',
  'DevOps Engineer',
  'Full Stack Engineer',
  'Product Manager',
  'CTO/Executive',
  'Founder',
  'Student/Researcher',
  'Other',
] as const;

export const USAGE_TAGS = [
  'Retrieval-Augmented Generation (RAG)',
  'Agentic AI System',
  'Recommendation System',
  'Analytics or Anomaly Detection',
  'Advanced Search (hybrid or multimodal)',
  'Other',
] as const;

export const USE_CASES = [
  'Build something new',
  'Learn / Explore',
  'Migrate from other solution',
  'Migrate from Qdrant Open Source',
] as const;

export const accountSchema = z.object({ name: validators.name });

export type FormAccountSchema = z.infer<typeof accountSchema>;

const companySchema = z
  .object({
    domain: validators.domain.optional(),
    name: validators.readableName,
  })
  .optional();

export const accountCompanySchema = z.object({
  company: companySchema,
});

export const updateAccountSchema = accountSchema.extend({
  // Clearing the company autocomplete writes `null` into the field (`CompanyAutocomplete.tsx`), and
  // `companySchema` is optional, not nullable — so the null has to be mapped before it validates.
  company: z.preprocess(
    (val: z.output<typeof companySchema>) => val ?? undefined,
    companySchema.superRefine((data, ctx) => {
      // Manually re-validate the nested field
      const result = companySchema.safeParse(data);
      // Set any errors on the parent context
      if (!result.success) {
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid company name',
        });
      }
    }),
  ),
});

export type FormAccountUpdateSchema = z.infer<typeof updateAccountSchema>;

export type FormAccountCompanySchema = z.infer<typeof accountCompanySchema>;

const demographicsSchema = z.object({
  roleInCompany: z.enum(ROLE_IN_COMPANY, { error: 'Role in company is required' }),
  usageTags: z.array(z.enum(USAGE_TAGS, { error: 'Usage tag is required' })).min(1, 'At least one usage is required'),
  useCase: z.enum(USE_CASES, { error: 'Use case is required' }),
});

export const updateUserProfileSchema = z
  .object({
    givenName: preprocessEmptyStringTo(validators.readableName.optional()),
    familyName: preprocessEmptyStringTo(validators.readableName.optional()),
    demographics: z.preprocess((val: z.infer<typeof demographicsSchema> | undefined) => {
      if (!val || typeof val !== 'object') {
        return undefined;
      }
      const isEmpty = Object.values<unknown>(val).every(
        (v) => (Array.isArray(v) && v.length === 0) || v === '' || v === undefined || v === null,
      );
      // Check if every value in the object is empty/null/undefined
      return isEmpty ? undefined : val;
    }, demographicsSchema.optional()),
  })
  .superRefine((data, ctx) => {
    if (!(data.givenName ?? data.familyName)) {
      const message = 'Either first name or last name must be filled in';
      ctx.addIssue({
        code: 'custom',
        message,
        path: ['givenName'],
      });
    }
  });

export type FormUserProfileUpdateSchema = z.infer<typeof updateUserProfileSchema>;
